import React from 'react';
import { isEmpty } from 'lodash';

import { useStyles } from '~/assets/styles';
import { Text } from '~/components/core';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Link from '~/components/core/Atomic/Link/Link';
import InnerCard from '~/components/core/Cards/InnerCard';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import { useCms } from '~/components/hooks/useCms';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import CancelIcon from '~/components/icons/CancelIcon';
import cn from '~/Utils/cn';
import { getLobDescription } from '~/Utils/lobUtils';

const UNKNOWN_OPTION_VALUE = 'unknown';
const ALL_OPTION_VALUE = 'all';

export interface FnolSubOrgLobFilterBarProps {
  subOrgIds: number[];
  lobs: string[];
  includeUnknownSubOrganization?: boolean;
  includeUnknownLob?: boolean;
  onSubOrgChange: (subOrgIds: number[], includeUnknown: boolean) => void;
  onLobChange: (lobs: string[], includeUnknown: boolean) => void;
  onClear: () => void;
  title?: string;
  showUnknownOption?: boolean;
}

interface SplitSelection {
  rest: string[];
  includeUnknown: boolean;
  isAll: boolean;
}

const splitSelection = (values: string[], showUnknownOption: boolean): SplitSelection => {
  const isAll = values.includes(ALL_OPTION_VALUE);
  const withoutAll = values.filter((v) => v !== ALL_OPTION_VALUE);
  if (!showUnknownOption) return { rest: withoutAll, includeUnknown: false, isAll };
  const includeUnknown = withoutAll.includes(UNKNOWN_OPTION_VALUE);
  const rest = withoutAll.filter((v) => v !== UNKNOWN_OPTION_VALUE);
  return { rest, includeUnknown, isAll };
};

// MUI Select skips renderValue when value is [], so we pass the 'all' sentinel
// to force it to render the label text ("All").
const withEmptyAsAll = (selected: string[]): string[] => (isEmpty(selected) ? [ALL_OPTION_VALUE] : selected);

export const FnolSubOrgLobFilterBar: React.FC<FnolSubOrgLobFilterBarProps> = ({
  subOrgIds,
  lobs,
  includeUnknownSubOrganization = false,
  includeUnknownLob = false,
  onSubOrgChange,
  onLobChange,
  onClear,
  title = 'Filter and search',
  showUnknownOption = true,
}) => {
  const { userOrganization } = useCms();
  const { classes } = useStyles();
  const { lobConfigurationsDict } = useLobConfiguration();
  const showSubOrgFilter = userOrganization.sub_organizations_enabled;

  const subOrgLabelById = React.useMemo(() => {
    const map = new Map<string, string>();
    (userOrganization.sub_organizations ?? []).forEach((so: { id: number; name?: string | null }) => {
      map.set(String(so.id), so.name ?? '');
    });
    return map;
  }, [userOrganization.sub_organizations]);

  const subOrgOptions = React.useMemo(() => {
    const sortedIds = (userOrganization.sub_organizations ?? [])
      .slice()
      .sort((a: { name?: string | null }, b: { name?: string | null }) =>
        (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
      )
      .map((so: { id: number }) => String(so.id));
    return showUnknownOption ? [UNKNOWN_OPTION_VALUE, ...sortedIds] : sortedIds;
  }, [userOrganization.sub_organizations, showUnknownOption]);

  const lobOptions = React.useMemo(() => {
    const ids = userOrganization.supported_claim_types ?? [];
    return showUnknownOption ? [UNKNOWN_OPTION_VALUE, ...ids] : ids;
  }, [userOrganization.supported_claim_types, showUnknownOption]);

  const subOrgValue = React.useMemo(() => {
    const ids = subOrgIds.map(String);
    const selected = showUnknownOption && includeUnknownSubOrganization ? [UNKNOWN_OPTION_VALUE, ...ids] : ids;
    return withEmptyAsAll(selected);
  }, [subOrgIds, includeUnknownSubOrganization, showUnknownOption]);

  const lobValue = React.useMemo(() => {
    const selected = showUnknownOption && includeUnknownLob ? [UNKNOWN_OPTION_VALUE, ...lobs] : lobs;
    return withEmptyAsAll(selected);
  }, [lobs, includeUnknownLob, showUnknownOption]);

  const handleSubOrgChange = (values: string[]) => {
    const { rest, includeUnknown, isAll } = splitSelection(values, showUnknownOption);
    if (isAll) {
      onSubOrgChange([], false);
      return;
    }
    onSubOrgChange(rest.map(Number), includeUnknown);
  };

  const handleLobChange = (values: string[]) => {
    const { rest, includeUnknown, isAll } = splitSelection(values, showUnknownOption);
    if (isAll) {
      onLobChange([], false);
      return;
    }
    onLobChange(rest, includeUnknown);
  };

  const renderSubOrgOption = (value: string) => {
    if (value === ALL_OPTION_VALUE) return 'All';
    if (value === UNKNOWN_OPTION_VALUE) return 'Unknown';
    return subOrgLabelById.get(value) ?? value;
  };

  const renderLobOption = (value: string) => {
    if (value === ALL_OPTION_VALUE) return 'All';
    if (value === UNKNOWN_OPTION_VALUE) return 'Unknown';
    return getLobDescription(value, lobConfigurationsDict);
  };

  const renderSelectedText =
    (renderOption: (value: string) => string) =>
    (selected: unknown): React.ReactNode => {
      const asArray = selected as string[];
      if (isEmpty(asArray)) return 'All';
      return asArray.map(renderOption).join(', ');
    };

  // stopPropagation prevents filter clicks from bubbling to an outer CollapsibleWrapper's
  // summary click handler (which toggles collapse state).
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <InnerCard className="p-8">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD}>
              {title}
            </Text>
          </Grid>
          {showSubOrgFilter && (
            <Grid item>
              <MultiSelectField<string, string>
                className={classes.textFieldRow}
                label="By Sub-Organization"
                options={subOrgOptions}
                value={subOrgValue}
                onChange={handleSubOrgChange}
                renderOption={renderSubOrgOption}
                renderValue={renderSelectedText(renderSubOrgOption)}
                addAllOption
                allOptionValue={ALL_OPTION_VALUE}
              />
            </Grid>
          )}
          <Grid item>
            <MultiSelectField<string, string>
              className={classes.textFieldRow}
              label="By Line of Business"
              options={lobOptions}
              value={lobValue}
              onChange={handleLobChange}
              renderOption={renderLobOption}
              renderValue={renderSelectedText(renderLobOption)}
              addAllOption
              allOptionValue={ALL_OPTION_VALUE}
            />
          </Grid>
          <Grid item>
            <Link
              href=""
              onClick={(e) => {
                e.preventDefault();
                onClear();
              }}
              className={cn(classes.alignedContainerForGrid, classes.secondaryLink, 'pt-1.5')}
            >
              <CancelIcon className={cn(classes.filledIcon, classes.leftButtonIcon)} />
              Clear all filters
            </Link>
          </Grid>
        </Grid>
      </InnerCard>
    </div>
  );
};
